import React from 'react'
import { useQueries, UseQueryOptions } from '@tanstack/react-query'
import { useAppSelector } from '@core/Store/hooks'
import { useFeature } from '@core/Utils/hooks/useFeature'
import {
  fetchKambiPendingTicketCount,
  fetchRacePendingTicketsCount,
  KambiPendingTicketsCountResponse,
  PendingTicketsCountResponse,
} from './helpers/api'
import {
  kambiPendingTicketsQuery,
  racingPendingTicketsQuery,
  racingPendingTicketsPollingQuery,
  kambiPendingTicketsPollingQuery,
} from './helpers/queries'
import type { PendingBetsCountHookReturn, RenderCount } from './types'
import { store } from '@core/Store'
import { setShouldAllowPendingRacingTicketCountPolling } from '@core/Areas/AppHeader/Store'
import { selectShouldAllowPendingRacingTicketCountPolling } from '@core/Areas/AppHeader/Store/selectors'

const enum LocalConstants {
  DefaultMaxCount = 99,
}

export const PendingTicketsCountProvider: React.FC<{ render: RenderCount }> = ({ render }) => {
  const isAppHeaderPendingBetsFeatureActive = useFeature('pending-bets-button-in-app-header')

  if (isAppHeaderPendingBetsFeatureActive) {
    return <ProvideSmartTicketsCount render={render} />
  }

  return <ProvideStandardTicketsCount render={render} />
}

// ================
// Local Components
// ================

/** Use standard pending count, NO global count visible */
const ProvideStandardTicketsCount: React.FC<{ render: RenderCount }> = ({ render }) => {
  const [
    { data: racing, isError: isRacingError, isLoading: isRacingLoading },
    { data: kambi, isError: isKambiError, isLoading: isKambiLoading },
  ] = useQueries({
    queries: [racingPendingTicketsQuery, kambiPendingTicketsQuery],
  })

  return render({
    ...mapResponseToCountReturn({ racing, kambi }),
    isRacingError,
    isRacingLoading,
    isKambiError,
    isKambiLoading,
  })
}

/** Use smart pending count with global count visible in App Header */
const ProvideSmartTicketsCount: React.FC<{ render: RenderCount }> = ({ render }) => {
  const isRacingPollingEnabled = useAppSelector(selectShouldAllowPendingRacingTicketCountPolling)

  const [
    {
      data: racing,
      isError: isRacingError,
      isLoading: isRacingLoading,
      isSuccess: isRacingSuccess,
    },
    { data: kambi, isError: isKambiError, isLoading: isKambiLoading },
  ] = useQueries({
    queries: [
      {
        ...racingPendingTicketsPollingQuery,
        enabled: isRacingPollingEnabled,
      } as UseQueryOptions,
      kambiPendingTicketsPollingQuery as UseQueryOptions,
    ],
  })

  React.useEffect(() => {
    if (isRacingSuccess) {
      const isCountAboveZero = !!(
        (racing as PendingTicketsCountResponse)?.pendingTicketsCount &&
        ((racing as PendingTicketsCountResponse).pendingTicketsCount ?? 0) > 0
      )
      store.dispatch(setShouldAllowPendingRacingTicketCountPolling(isCountAboveZero))
    }
  }, [isRacingSuccess, racing])

  return render({
    ...mapResponseToCountReturn({
      racing: racing as PendingTicketsCountResponse,
      kambi: kambi as KambiPendingTicketsCountResponse,
    }),
    isRacingError,
    isRacingLoading,
    isKambiError,
    isKambiLoading,
  })
}

// =============
// Local Helpers
// =============

function mapResponseToCountReturn(data: {
  racing?: Awaited<ReturnType<typeof fetchRacePendingTicketsCount>>
  kambi: Awaited<ReturnType<typeof fetchKambiPendingTicketCount>>
}): Pick<
  PendingBetsCountHookReturn,
  'pendingBetsCount' | 'pendingRacingCount' | 'pendingSportsCount' | 'doesCountExceedMax'
> {
  const pendingRacingCount = data.racing?.pendingTicketsCount ?? null
  const pendingSportsCount = data.kambi ?? null

  const pendingBetsCount =
    pendingRacingCount !== null && pendingSportsCount !== null
      ? Math.min(pendingRacingCount + pendingSportsCount, LocalConstants.DefaultMaxCount)
      : null

  const doesCountExceedMax = !!(
    pendingBetsCount && pendingBetsCount > LocalConstants.DefaultMaxCount
  )

  return { pendingBetsCount, pendingRacingCount, pendingSportsCount, doesCountExceedMax }
}
